import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import KindredShareCard from "@/components/KindredShareCard";
import { fetchExternalLinkPreview } from "@/lib/externalLinkPreview";
import { cn } from "@/lib/utils";
import { parseKindredShareContent } from "@/lib/kindredShare";
import {
  getExternalUrlDomain,
  getKindredAppRoute,
  getPrimaryExternalUrl,
  shouldCollapseKinfeedPost,
  splitTextByExternalUrls,
  type ExternalLinkPreview,
} from "../../shared/community";

interface KindredSharedContentProps {
  content: string | null | undefined;
  textClassName?: string;
  linkPreview?: ExternalLinkPreview | null;
  collapsible?: boolean;
}

const LinkedText = ({
  text,
  className,
  collapsed = false,
}: {
  text: string;
  className?: string;
  collapsed?: boolean;
}) => {
  const navigate = useNavigate();
  const segments = useMemo(() => splitTextByExternalUrls(text), [text]);

  if (!text) {
    return null;
  }

  return (
    <p className={cn("whitespace-pre-wrap break-words", collapsed && "line-clamp-8", className)}>
      {segments.map((segment, index) =>
        segment.type === "url" ? (() => {
          const appRoute = getKindredAppRoute(segment.url);
          return (
            <a
              key={`${segment.url}-${index}`}
              href={appRoute ?? segment.url}
              target={appRoute ? undefined : "_blank"}
              rel={appRoute ? undefined : "noopener noreferrer"}
              className="font-semibold text-primary underline underline-offset-2"
              onClick={appRoute ? (event) => {
                event.preventDefault();
                navigate(appRoute);
              } : undefined}
            >
              {segment.text}
            </a>
          );
        })() : (
          <span key={`${segment.text}-${index}`}>{segment.text}</span>
        ),
      )}
    </p>
  );
};

export const ExternalLinkPreviewCard = ({
  url,
  storedPreview,
}: {
  url: string | null;
  storedPreview?: ExternalLinkPreview | null;
}) => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<ExternalLinkPreview | null>(storedPreview ?? null);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setImageFailed(false);
    setPreview(storedPreview ?? null);

    if (!url || storedPreview?.url === url) {
      return () => {
        active = false;
      };
    }

    void fetchExternalLinkPreview(url).then((nextPreview) => {
      if (active) {
        setPreview(nextPreview);
      }
    });

    return () => {
      active = false;
    };
  }, [storedPreview, url]);

  if (!url) {
    return null;
  }

  const displayPreview = preview ?? {
    url,
    domain: getExternalUrlDomain(url),
    title: null,
    description: null,
    imageUrl: null,
  };
  const title = displayPreview.title || displayPreview.domain;
  const description = displayPreview.description || displayPreview.url;
  const imageUrl = displayPreview.imageUrl && !imageFailed ? displayPreview.imageUrl : null;
  const appRoute = getKindredAppRoute(displayPreview.url);

  return (
    <a
      href={appRoute ?? displayPreview.url}
      target={appRoute ? undefined : "_blank"}
      rel={appRoute ? undefined : "noopener noreferrer"}
      onClick={appRoute ? (event) => {
        event.preventDefault();
        navigate(appRoute);
      } : undefined}
      className="mt-2 flex min-w-0 overflow-hidden rounded-2xl border border-border bg-card text-left no-underline transition hover:border-primary/35"
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="h-24 w-28 shrink-0 object-cover"
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="flex h-24 w-28 shrink-0 items-center justify-center bg-primary/10 text-sm font-semibold text-primary">
          Link
        </span>
      )}
      <span className="min-w-0 flex-1 px-3 py-2.5">
        <span className="block text-[11px] font-semibold uppercase text-muted-foreground">
          {displayPreview.domain}
        </span>
        <span className="mt-1 block line-clamp-2 text-sm font-semibold leading-5 text-foreground">
          {title}
        </span>
        {description ? (
          <span className="mt-1 block line-clamp-2 text-xs leading-4 text-muted-foreground">
            {description}
          </span>
        ) : null}
      </span>
    </a>
  );
};

const KindredSharedContent = ({ content, textClassName, linkPreview, collapsible = false }: KindredSharedContentProps) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const parsed = parseKindredShareContent(content);
  const text = parsed.payload ? parsed.text : content ?? "";
  const primaryExternalUrl = getPrimaryExternalUrl(text);
  const canCollapse = collapsible && shouldCollapseKinfeedPost(text);
  const collapsed = canCollapse && !expanded;

  useEffect(() => {
    setExpanded(false);
  }, [content]);

  const postText = text ? (
    <>
      <LinkedText text={text} className={textClassName} collapsed={collapsed} />
      {canCollapse ? (
        <button
          type="button"
          aria-expanded={expanded}
          className="mt-1 text-sm font-semibold text-primary hover:underline"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((current) => !current);
          }}
        >
          {expanded ? "See less" : "See more"}
        </button>
      ) : null}
    </>
  ) : null;

  if (!parsed.payload) {
    return (
      <div>
        {postText}
        <ExternalLinkPreviewCard url={primaryExternalUrl} storedPreview={linkPreview} />
      </div>
    );
  }

  return (
    <div>
      {postText}
      <ExternalLinkPreviewCard url={primaryExternalUrl} storedPreview={linkPreview} />
      <KindredShareCard payload={parsed.payload} onOpen={() => navigate(parsed.payload.route)} />
    </div>
  );
};

export default KindredSharedContent;
